<template>
  <div style="width: 100%; position: relative">
    <ckeditor
      v-model="editorModel"
      :editor="editor"
      :ref="name"
      :name="name"
      :config="editorConfig"
      :autoFocus="autofocus"
      :disabled="disabled"
      :key="updateKey"
      @ready="onEditorReady"
      @keydown.native="leaveComment"
      @keyup.native="countTextLength"
      @input="checkUsers"
      @blur="$emit('blur')"
      class="el-textarea__inner"
    />
    <span
      v-if="maxLength && textLength && showLength"
      :class="['text-limit', disabled ? 'is-disabled' : '']"
    >
      {{ `${textLength}/${maxLength}` }}
    </span>
    <Alert
      v-show="showWarningField"
      type="warning"
      :text="tagWarningText"
      iconColor="#FFB70F"
      class="el-textarea__warning"
    />
  </div>
</template>

<script>
import { getController } from '@/controllers/request.js';
import { MODULE } from '@/utils/constants';
import { mixin } from '@/utils/mixins';
import { Bold, Italic, Underline } from '@ckeditor/ckeditor5-basic-styles';
import '@ckeditor/ckeditor5-basic-styles/build/translations/ru';
import { BalloonEditor } from '@ckeditor/ckeditor5-editor-balloon';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { List } from '@ckeditor/ckeditor5-list';
import '@ckeditor/ckeditor5-list/build/translations/ru';
import { Mention } from '@ckeditor/ckeditor5-mention';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import Alert from 'Elements/Alert/Alert';

export default {
  mixins: [mixin],
  components: {
    ckeditor: CKEditor.component,
    Alert
  },
  props: {
    internal: {
      type: Object,
      default: undefined
    },
    mentions: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    name: String,
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    recruits: {
      type: Boolean,
      default: false
    },
    showLength: {
      type: Boolean,
      default: true
    },

    placeholder: String,
    minLength: Number,
    maxLength: Number
  },
  mounted() {},
  watch: {
    recruits() {
      this.forbiddenRoleUsers = [];
      this.checkUsers(this.editorModel);
    }
  },
  data() {
    return {
      editor: BalloonEditor,
      feedItems: [],
      editorConfig: {
        placeholder: this.placeholder,
        plugins: this.plugins(),
        toolbar: ['bold', 'italic', 'underline', 'bulletedList'],
        language: 'ru',
        mention: {
          dropdownLimit: 9999,
          feeds: [
            {
              marker: '@',
              feed: queryText => this.getFeedItems(queryText),
              itemRenderer: this.customItemRenderer
            }
          ]
        }
      },
      textLength: 0,
      forbiddenRoleUsers: [],
      loadMoreRequestSent: false,
      loadMoreIteration: 0,
      needToLoadMore: false,
      updateKey: 0,
      focusIndex: null,
      searchString: '',
      lazyLoadLimit: 50
    };
  },
  computed: {
    showWarningField() {
      return this.forbiddenRoleUsers.length;
    },
    tagWarningText() {
      return `Следующие пользователи не увидят комментарий из-за ограничения роли: ${this.forbiddenRoleUsers
        .map(user => user.substring(1, user.length))
        .join(', ')}`;
    },
    editorModel: {
      set(value) {
        if (this.maxLength && this.textLength == this.maxLength) return false;
        this.$emit('change', this.formatHtml(value));
      },
      get() {
        return this.formatEditorHtml(this.value)
          .replace(/\\"/g, '')
          .replace(' class="cdx-underline"', '');
      }
    }
  },
  methods: {
    plugins() {
      const pluginsArray = [
        Essentials,
        Paragraph,
        Bold,
        Italic,
        Underline,
        List
      ];
      if (this.mentions) {
        pluginsArray.push(Mention);
        pluginsArray.push(this.mentionCustomization);
      }
      return pluginsArray;
    },
    checkUsers(text) {
      const stringToSearch = 'data-user-id';
      if (!this.mentions && !text.includes(stringToSearch)) {
        return;
      }

      const mentionUsersSet = new Set();
      const mentionUsersMap = new Map();

      //ищем индексы вхождения айди выбрынных для уведомления пользователей
      let indexOfSearchString = -1;
      let indexOfTagEnd = -1;
      while (
        (indexOfSearchString = text.indexOf(
          stringToSearch,
          indexOfSearchString + 1
        )) !== -1
      ) {
        //на каждый найденный индекс записываем часть общей строки с айди и именем пользователя
        //в сет - чтобы избежать дубликатов
        indexOfTagEnd = text.indexOf('<', indexOfSearchString);
        mentionUsersSet.add(
          text
            .substring(
              indexOfSearchString + stringToSearch.length + 2,
              indexOfTagEnd
            )
            .replace('"', '')
        );
      }
      //форматируем найденый сет в коллекцию для удобства
      mentionUsersSet.forEach(user => {
        const a = user.split('>');
        mentionUsersMap.set(a[0], a[1]);
      });
      //в случае если тег упомянутого пользователя удаляется,
      //и он был в списке тех, у кого не было доступа к упоминаниям,
      //удаляем его из уведомления
      const currentForbiddenUsers = new Set(this.forbiddenRoleUsers);
      const diff = currentForbiddenUsers.difference(
        new Set(mentionUsersMap.values())
      );
      if (diff.size) {
        this.forbiddenRoleUsers = this.forbiddenRoleUsers.filter(
          val => !diff.has(val)
        );
      }
      //на каждого выбранного пользователя отправляем запрос с проверкой на
      //доступность его роли для упоминаний
      mentionUsersMap.forEach(async (value, key) => {
        const params = {
          action: 'check_access_user_to_comment',
          module: MODULE.HRPAC_COMMENTS,
          parent_id: this.internal?.parent_id || this.$route.params?.id,
          parent_type: this.internal?.parent_type || this.$route.meta.module,
          user_id: key,
          to_recruits: this.recruits,
          hrpac_vacancy_id: this.internal?.hrpac_vacancy_id || null
        };
        const { data } = await getController({ params });
        //если у пользователя нет прав на упоминания, добавляем его в список
        //для отображения в уведомлении
        if (
          data[0] == 'no_access' &&
          !this.forbiddenRoleUsers.includes(value)
        ) {
          this.forbiddenRoleUsers.push(value);
        }
      });
    },
    onEditorReady(editor) {
      //когда компонент отрендерен после пагинации,
      //нужно установить фокус на элемент и положение каретки до рендера
      if (this.focusIndex) {
        editor.focus();
        editor.model.change(writer => {
          const insertPosition =
            editor.model.document.selection.getFirstPosition();
          insertPosition.path[1] = this.focusIndex;
          writer.setSelection(writer.createPositionAt(insertPosition, 0));
        });
        this.focusIndex = null;
      }
    },
    setPaginationEventListener() {
      const interval = setInterval(() => {
        const mentionList = document.querySelector('.ck-mentions');
        if (!mentionList) return;
        clearInterval(interval);
        mentionList.addEventListener('scroll', this.loadMoreByScroll);
      }, 100);
    },
    getFeedItems(queryText) {
      //если поиск начинается заново, нужно очистить список данных
      //и итерацию
      if (queryText != this.searchString && this.searchString !== '') {
        this.feedItems = [];
        this.loadMoreIteration = 0;
      }

      return new Promise(resolve => {
        //если текст поиска тот же, что и был с последнего запроса,
        //то ничего не делаем (чтобы избежать новой загрузки данных)
        if (queryText == this.searchString) {
          resolve(this.feedItems);

          //если после пагинации загрузилось максимальное кол-во
          //пользователей, то предполагается, что нужна доп.пагинация,
          //поэтому вешаем слушатель события скролла
          if (
            !(this.feedItems.length % this.lazyLoadLimit) &&
            this.feedItems.length !== 0
          ) {
            this.loadMoreIteration = this.feedItems.length / this.lazyLoadLimit;
            this.setPaginationEventListener();
          }

          return;
        }

        //загружаем новые данные
        setTimeout(async () => {
          const params = {
            action: 'get_user_list',
            module: MODULE.USERS,
            offset: 0,
            limit: this.lazyLoadLimit,
            search_string: queryText,
            to_pdf: true
          };
          const { data } = await getController({ params });

          //приводим данные к нужному для отображения виду
          const itemsToDisplay = this.convertMentions(data);

          //сохраняем текст запроса и полученные данные
          this.searchString = queryText;
          this.feedItems = itemsToDisplay;
          //и выводим список
          resolve(itemsToDisplay);

          //если при первой загрузке загрузилось максимальное кол-во
          //пользователей, то предполагается, что нужна пагинация
          if (itemsToDisplay.length === this.lazyLoadLimit) {
            this.loadMoreIteration++;
            this.setPaginationEventListener();
          }
        }, 100);
      });
    },
    mentionCustomization(editor) {
      editor.conversion.for('upcast').elementToAttribute({
        view: {
          name: 'span',
          key: 'data-mention',
          classes: 'mention',
          attributes: {
            'data-user-id': true
          }
        },
        model: {
          key: 'mention',
          value: viewItem => {
            const mentionAttribute = editor.plugins
              .get('Mention')
              .toMentionAttribute(viewItem, {
                userId: viewItem.getAttribute('data-user-id')
              });

            return mentionAttribute;
          }
        },
        converterPriority: 'high'
      });

      editor.conversion.for('downcast').attributeToElement({
        model: 'mention',
        view: (modelAttributeValue, { writer }) => {
          if (!modelAttributeValue) {
            return;
          }

          return writer.createAttributeElement(
            'span',
            {
              class: 'mention',
              'data-mention': modelAttributeValue.id,
              'data-user-id': modelAttributeValue.userId
            },
            {
              priority: 20,
              id: modelAttributeValue.uid
            }
          );
        },
        converterPriority: 'high'
      });
    },
    convertMentions(data) {
      return Object.entries(data).map(([key, value]) => {
        return {
          id: `@${value}`,
          userId: key,
          name: value
        };
      });
    },
    async loadMoreByScroll(e) {
      //проверяем чтобы скролл был в самом низу и запрос ещё не был отправлен
      if (
        (e.target.scrollHeight <=
          Math.round(e.target.scrollTop + e.target.clientHeight) ||
          e.target.scrollHeight <=
            Math.round(e.target.scrollTop + e.target.clientHeight) + 1) &&
        !this.loadMoreRequestSent
      ) {
        //загружаем новые данные
        this.loadMoreRequestSent = true;
        const params = {
          action: 'get_user_list',
          module: MODULE.USERS,
          offset: this.loadMoreIteration * this.lazyLoadLimit,
          limit: this.lazyLoadLimit,
          search_string: this.searchString,
          to_pdf: true
        };
        const { data } = await getController({ params });

        //приводим данные к нужному для отображения виду
        const itemsToDisplay = this.convertMentions(data);

        //сохраняем полученные данные
        this.feedItems = [...this.feedItems, ...itemsToDisplay];
        //сохраняем индекс текущего положения каретки
        const domEditableElement = document.querySelector(
          '.ck-editor__editable'
        );
        const editorInstance = domEditableElement.ckeditorInstance;
        this.focusIndex =
          editorInstance.model.document.selection.getFirstPosition().path[1];
        this.loadMoreRequestSent = false;
        //обновляем компонент для отображения загруженных данных
        this.updateKey++;
      }
    },
    formatEditorHtml(text) {
      if (typeof text === 'string' && text) {
        const replacement = [
          [/&quot;/g, '"'],
          [/&#039;/g, "'"],
          [/&#38;/g, '"'],
          [/&#96;/g, '`'],
          [/%3A/g, ':']
        ];
        return text
          .replace(...replacement[0])
          .replace(...replacement[1])
          .replace(...replacement[2])
          .replace(...replacement[3])
          .replace(...replacement[4]);
      }
      return text;
    },
    leaveComment(event) {
      if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.$emit('leaveComment');
      }
    },
    countTextLength() {
      if (!this.maxLength) return;

      let count = 0;
      let max = this.maxLength;

      this.$refs[this.name].$el.childNodes.forEach(node => {
        // подсчет длины текста без учета вложенности html-тегов
        const text = node.nodeType !== 3 ? node.innerText : node.nodeValue;
        count += text.length;

        if (count > max) {
          const num = max - count;

          if (node.innerText) {
            node.innerText = node.innerText.replace(
              node.innerText.slice(num),
              ''
            );
          }
          if (node.nodeValue) {
            node.nodeValue = node.nodeValue.replace(
              node.nodeValue.slice(num),
              ''
            );
          }
          count = max;
        }
      });

      this.textLength = count;

      if (count == max) {
        this.editorModel = this.$refs[this.name].$el.innerHTML;
        this.value = this.$sanitize(this.$refs[this.name].$el.innerHTML);
      }

      return count;
    },
    customItemRenderer(item) {
      const itemElement = document.createElement('span');

      itemElement.classList.add('custom-item');
      itemElement.id = `mention-list-item-id-${item.userId}`;
      itemElement.textContent = `${item.name} `;

      return itemElement;
    }
  }
};
</script>

<style lang="scss" scoped>
.el-textarea {
  &__inner {
    padding: 0;
    border: none;

    &:hover::after {
      background-color: #f6f8f9 !important;
      color: #f6f8f9 !important;
    }
  }

  &__warning {
    margin-top: 16px;
  }
}
</style>

<style lang="scss">
.ck {
  background-color: $white;
  position: relative;
  --ck-color-mention-background: transparent;
  --ck-color-mention-text: #0075db;

  &-list.ck-mentions {
    background: $white !important;

    &::-webkit-scrollbar {
      width: 8px !important;
      background: white !important;
    }

    & > .ck-list__item {
      background: white !important;

      &:first-child {
        margin-top: 6px;
      }

      &:last-child {
        margin-bottom: 6px;
      }
    }
  }

  &-button.custom-item {
    display: flex;
    background: $white !important;
    color: $grey80 !important;
    padding: 0 12px !important;
    font-size: 16px;
    line-height: 40px;
    font-family: 'Roboto', sans-serif;

    &:hover {
      background: $grey8 !important;
    }
  }

  &:hover::after {
    background-color: transparent;
  }

  &::-webkit-scrollbar {
    width: 16px !important;
  }

  .ck-placeholder::before {
    color: $black-60;
  }

  & ~ .text-limit {
    position: absolute;
    bottom: 1px;
    left: 1px;
    text-align: right;
    line-height: 24px;
    background-color: $white;
    width: calc(100% - 32px);
    border-radius: 4px;
  }

  &.ck-focused,
  &.ck-blurred {
    border-radius: 4px !important;
    border: 1px solid $grey16 !important;
    box-shadow: none !important;
    min-height: 64px;
    max-height: 480px;
    overflow-y: auto;
    word-break: break-word;

    &:after {
      z-index: 2;
      content: '';
      width: 28px;
      height: 28px;
      display: inline-block;
      position: absolute;
      background-size: 24px 24px;
      background-repeat: no-repeat;
      bottom: 65px;
      right: 1px;
      color: $white !important;
      background-color: white;

      border-radius: 4px;
      pointer-events: none;
    }

    &:focus {
      border-color: $blue-120 !important;
      border-radius: 4px;
    }

    &:invalid {
      border: 1px solid $red !important;
      border-radius: 4px;
    }
  }

  &.ck-read-only {
    background-color: $black-10;
    border: 1px solid $black-30 !important;

    &:hover {
      border: 1px solid $grey16 !important;
    }

    &:after {
      content: '';
      width: 28px;
      height: 28px;
      display: inline-block;
      position: absolute;
      background-size: 24px 24px;
      background-repeat: no-repeat;
      bottom: 1px;
      right: 1px;
      background-color: $black-10 !important;
      border-radius: 4px;
      pointer-events: none;
    }
  }

  &.ck-editor__editable_inline {
    padding: 8px 12px 24px 12px !important;
    min-height: 40px;

    p {
      margin: 0 !important;
    }

    & > p {
      margin: 0;
      font-size: 16px;
      line-height: 24px;
      color: $black-200;
    }
  }

  &.ck-reset_all,
  &.ck-reset_all *:not(.ck-tooltip):not(.ck-tooltip__text) {
    background: $blue-130;
    color: $white;
  }

  &.ck-balloon-panel_arrow_s:after,
  &.ck-balloon-panel_arrow_s:before {
    content: unset !important;
  }

  &.ck-button:hover,
  &.ck-button:hover > *:not(.ck-tooltip):not(.ck-tooltip__text) {
    background: $blue-150 !important;
  }

  .ck-balloon-rotator {
    border-radius: 4px !important;
  }

  ol,
  ul {
    list-style: inside;
  }

  .ck-icon {
    color: $blue-130 !important;
  }

  &:hover ~ .text-limit {
    background-color: $black-10;
  }
}

.user-options {
  background: $white;
  z-index: 9999;
  width: 100%;
  padding: 4px 0;
  border-radius: 4px;
  position: absolute;
  top: 0;
  left: 0;
  max-height: 200px;
  overflow: auto;

  li {
    font-size: 14px;
    line-height: 20px;
    padding: 8px 12px;
    cursor: pointer;

    &:hover {
      background-color: #f6f8f9;
    }
  }

  ul,
  li {
    list-style: none;
  }
}

.blue {
  color: red;
}
</style>
